/* Feb-2026 · General Ledger + Chart of Accounts
 *
 * Double-entry bookkeeping surface for the Finance admin. Renders three
 * stacked regions:
 *
 *   1. Chart of Accounts — grouped by class (Asset · Liability · Equity
 *      · Revenue · Expense), each row showing the current balance from
 *      the trial-balance rollup.
 *   2. New Journal Entry — multi-line debit/credit form with live
 *      validation (debits must equal credits before submit enables).
 *   3. Recent Entries — filterable by account code + since date.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Landmark, PlusCircle, Trash2, Loader2, RefreshCw, Filter, Scale, BookText, RotateCcw,
} from 'lucide-react';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import { evaluateLedgerEntry } from '@/lib/ledgerEntryValidation';
import { ResizableLedgerWorkspace } from './ResizableLedgerWorkspace';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const CLASS_META = {
  asset:     { label: 'Assets',      range: '100-199', tint: 'text-success' },
  liability: { label: 'Liabilities', range: '200-299', tint: 'text-warning' },
  equity:    { label: 'Equity',      range: '300-399', tint: 'text-primary' },
  revenue:   { label: 'Revenue',     range: '400-499', tint: 'text-success' },
  expense:   { label: 'Expenses',    range: '500-599', tint: 'text-destructive' },
};

const CLASS_ORDER = ['asset', 'liability', 'equity', 'revenue', 'expense'];

function fmtUSD(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const sign = v < 0 ? '−' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function makeBlankLine() {
  return { account_code: '', debit_usd: '', credit_usd: '', memo: '' };
}

export default function LedgerPanel() {
  const [accounts, setAccounts]   = useState([]);
  const [balances, setBalances]   = useState([]);
  const [totals,   setTotals]     = useState({ debits: 0, credits: 0, is_balanced: true });
  const [entries,  setEntries]    = useState([]);
  const [filterAcct, setFilterAcct] = useState('');
  const [busy, setBusy]           = useState(false);

  const [entryMemo, setEntryMemo] = useState('');
  const [entryRef,  setEntryRef]  = useState('');
  const [lines,     setLines]     = useState([makeBlankLine(), makeBlankLine()]);
  const [saving,    setSaving]    = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const [balanceAccount, setBalanceAccount] = useState('');
  const formRef = useRef(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [ac, tb, en] = await Promise.all([
        http.get('/admin/gl/accounts').then((r) => r.data),
        http.get('/admin/gl/trial-balance').then((r) => r.data),
        http.get('/admin/gl/entries', { params: filterAcct ? { account_code: filterAcct } : {} }).then((r) => r.data),
      ]);
      setAccounts(ac.accounts || []);
      setBalances(tb.rows || []);
      setTotals(tb.totals || { debits: 0, credits: 0, is_balanced: true });
      setEntries(en.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ledger.');
    } finally { setBusy(false); }
  }, [filterAcct]);

  useEffect(() => { reload(); }, [reload]);

  const byClass = useMemo(() => {
    const grouped = {};
    for (const b of balances) (grouped[b.class] = grouped[b.class] || []).push(b);
    return grouped;
  }, [balances]);

  const entryValidation = useMemo(
    () => evaluateLedgerEntry(entryMemo, lines),
    [entryMemo, lines],
  );
  const {
    valid: canSubmit,
    debitTotal,
    creditTotal,
    postingLines,
  } = entryValidation;

  const updateLine = (idx, patch) =>
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines((prev) => [...prev, makeBlankLine()]);
  const removeLine = (idx) =>
    setLines((prev) => prev.length > 2 ? prev.filter((_, i) => i !== idx) : prev);

  const imbalance = Math.round((debitTotal - creditTotal) * 100) / 100;
  const balancingSide = imbalance > 0 ? 'credit' : 'debit';
  const balancingAmount = Math.abs(imbalance);
  const addBalancingLine = () => {
    if (!balanceAccount || balancingAmount < 0.005) return;
    const patch = {
      account_code: balanceAccount,
      debit_usd: balancingSide === 'debit' ? balancingAmount.toFixed(2) : '',
      credit_usd: balancingSide === 'credit' ? balancingAmount.toFixed(2) : '',
      memo: 'Balancing line',
    };
    setLines((prev) => {
      const blank = prev.findIndex((line) => !line.account_code && !line.debit_usd && !line.credit_usd);
      if (blank < 0) return [...prev, patch];
      return prev.map((line, index) => index === blank ? patch : line);
    });
    setBalanceAccount('');
  };

  const submit = async () => {
    if (!canSubmit) {
      toast.error(entryValidation.message);
      return;
    }
    setSaving(true);
    try {
      await http.post('/admin/gl/entries', {
        memo: entryValidation.cleanMemo,
        reference: entryRef.trim() || undefined,
        lines: postingLines.map((line) => ({
          ...line,
          memo: line.memo || undefined,
        })),
      });
      toast.success('Journal entry posted.');
      setEntryMemo(''); setEntryRef('');
      setLines([makeBlankLine(), makeBlankLine()]);
      await reload();
      window.dispatchEvent(new CustomEvent('finance:updated', { detail: { source: 'ledger' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post entry.');
    } finally { setSaving(false); }
  };

  const correctEntry = async (entry) => {
    const ok = await openConfirm({
      title: 'Reverse and correct entry?',
      description: 'Posted accounting entries are never overwritten. This creates an auditable reversal and loads a new correction draft.',
      confirmLabel: 'Create reversal',
      tone: 'destructive',
    });
    if (!ok) return;
    setCorrecting(entry.id);
    try {
      await http.post(`/admin/gl/entries/${encodeURIComponent(entry.id)}/reverse`, {
        reason: `Correction initiated for ${entry.memo || entry.id}`,
      });
      setEntryMemo(`Correction of ${entry.id}: ${entry.memo || ''}`);
      setEntryRef(entry.reference || entry.id);
      setLines((entry.lines || []).map((line) => ({
        account_code: line.account_code,
        debit_usd: line.debit_usd ? String(line.debit_usd) : '',
        credit_usd: line.credit_usd ? String(line.credit_usd) : '',
        memo: line.memo || '',
      })));
      toast.success('Reversal posted. Review and post the correction draft.');
      await reload();
      window.dispatchEvent(new CustomEvent('finance:updated', { detail: { source: 'ledger-reversal' } }));
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not reverse this entry.');
    } finally { setCorrecting(null); }
  };

  return (
    <ResizableLedgerWorkspace>
    <section data-testid="ledger-panel" className="space-y-4">
      {/* Trial-balance header strip */}
      <div className="rounded-md border border-border bg-card p-3" data-testid="ledger-trial-balance-strip">
        <div className="flex flex-wrap items-center gap-3">
          <Scale className="h-4 w-4 text-primary" />
          <div className="text-[12.5px] font-semibold">Trial Balance</div>
          <span className="font-mono-stat text-[12px]">
            Debits: <span className="text-primary">{fmtUSD(totals.debits)}</span>
          </span>
          <span className="font-mono-stat text-[12px]">
            Credits: <span className="text-primary">{fmtUSD(totals.credits)}</span>
          </span>
          <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${
            totals.is_balanced ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'
          }`}>
            {totals.is_balanced ? 'Balanced' : 'Out of balance'}
          </span>
          <button onClick={reload} disabled={busy}
                  className="ml-auto inline-flex h-7 items-center gap-1 rounded-sm border border-border bg-background px-2 text-[11px] shadow-btn hover:shadow-btn-hover disabled:opacity-60"
                  data-testid="ledger-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
          </button>
        </div>
      </div>

      {/* Chart of Accounts */}
      <div className="rounded-md border border-border bg-card p-3" data-testid="ledger-chart-of-accounts">
        <div className="mb-2 flex items-center gap-2">
          <Landmark className="h-4 w-4 text-primary" />
          <div className="text-[12.5px] font-semibold">Chart of Accounts</div>
          <span className="text-[10.5px] text-muted-foreground">Click a code to filter recent entries.</span>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {CLASS_ORDER.map((cls) => {
            const meta = CLASS_META[cls];
            const rows = byClass[cls] || [];
            return (
              <div key={cls} className="rounded-sm border border-border bg-panel-header/30 p-2" data-testid={`ledger-class-${cls}`}>
                <div className={`flex items-center justify-between text-[10.5px] uppercase tracking-wider ${meta.tint}`}>
                  <span className="font-semibold">{meta.label}</span>
                  <span className="font-mono-stat text-muted-foreground">{meta.range}</span>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {rows.map((r) => (
                    <button
                      key={r.code}
                      type="button"
                      onClick={() => setFilterAcct(filterAcct === r.code ? '' : r.code)}
                      className={`flex w-full items-center justify-between rounded-sm px-1.5 py-1 text-left text-[11px] transition-colors ${
                        filterAcct === r.code ? 'bg-primary/15 text-primary' : 'hover:bg-secondary/60'
                      }`}
                      data-testid={`ledger-account-${r.code}`}
                      title={`${r.entry_count} entries`}
                    >
                      <span className="truncate"><span className="font-mono-stat text-muted-foreground">{r.code}</span> {r.name}</span>
                      <span className="ml-2 shrink-0 font-mono-stat text-[10.5px]">{fmtUSD(r.balance)}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* New Journal Entry form */}
      <div ref={formRef} className="rounded-md border border-border bg-card p-3" data-testid="ledger-new-entry">
        <div className="mb-2 flex items-center gap-2">
          <PlusCircle className="h-4 w-4 text-primary" />
          <div className="text-[12.5px] font-semibold">New Journal Entry</div>
          <span className="text-[10.5px] text-muted-foreground">Debits must equal credits.</span>
          <span className="ml-auto text-[10px] text-muted-foreground">Corrections use reversal entries to preserve the audit trail.</span>
        </div>
        <div className="mb-2 grid gap-2 md:grid-cols-2">
          <input
            type="text" value={entryMemo}
            onChange={(e) => setEntryMemo(e.target.value)}
            placeholder="Memo (e.g. Sept rent, Author payout batch #14)"
            className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
            data-testid="ledger-entry-memo"
          />
          <input
            type="text" value={entryRef}
            onChange={(e) => setEntryRef(e.target.value)}
            placeholder="Reference (invoice #, PO #, Stripe charge id)"
            className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
            data-testid="ledger-entry-reference"
          />
        </div>
        <div className="space-y-1.5">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5" data-testid={`ledger-entry-line-${i}`}>
              <select
                value={l.account_code}
                onChange={(e) => updateLine(i, { account_code: e.target.value })}
                className="col-span-4 h-8 rounded-sm border border-border bg-background px-2 text-[11.5px] outline-none focus:border-primary"
                data-testid={`ledger-entry-line-${i}-account`}
              >
                <option value="">Select account…</option>
                {CLASS_ORDER.flatMap((cls) => {
                  const rows = accounts.filter((a) => a.class === cls);
                  return rows.length ? [
                    <optgroup key={cls} label={`${CLASS_META[cls].label} · ${CLASS_META[cls].range}`}>
                      {rows.map((a) => (
                        <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>
                      ))}
                    </optgroup>
                  ] : [];
                })}
              </select>
              <input
                type="number" min="0" step="0.01" value={l.debit_usd}
                onChange={(e) => updateLine(i, { debit_usd: e.target.value, credit_usd: '' })}
                placeholder="Debit $"
                className="col-span-2 h-8 rounded-sm border border-border bg-background px-2 text-right text-[11.5px] font-mono-stat outline-none focus:border-primary"
                data-testid={`ledger-entry-line-${i}-debit`}
              />
              <input
                type="number" min="0" step="0.01" value={l.credit_usd}
                onChange={(e) => updateLine(i, { credit_usd: e.target.value, debit_usd: '' })}
                placeholder="Credit $"
                className="col-span-2 h-8 rounded-sm border border-border bg-background px-2 text-right text-[11.5px] font-mono-stat outline-none focus:border-primary"
                data-testid={`ledger-entry-line-${i}-credit`}
              />
              <input
                type="text" value={l.memo}
                onChange={(e) => updateLine(i, { memo: e.target.value })}
                placeholder="Line memo (optional)"
                className="col-span-3 h-8 rounded-sm border border-border bg-background px-2 text-[11.5px] outline-none focus:border-primary"
                data-testid={`ledger-entry-line-${i}-memo`}
              />
              <button
                type="button"
                onClick={() => removeLine(i)}
                disabled={lines.length <= 2}
                className="col-span-1 rounded-sm text-muted-foreground hover:bg-secondary hover:text-destructive disabled:opacity-30"
                data-testid={`ledger-entry-line-${i}-remove`}
              >
                <Trash2 className="mx-auto h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        {balancingAmount >= 0.005 && (debitTotal > 0 || creditTotal > 0) && (
          <div
            className="mt-2 flex flex-wrap items-center gap-2 rounded-sm border border-warning/50 bg-warning/10 px-2.5 py-2 text-[11.5px]"
            data-testid="ledger-balance-suggestion"
            role="dialog"
            aria-label="Balancing entry suggestion"
          >
            <Scale className="h-3.5 w-3.5 text-warning" />
            <span data-testid="ledger-balance-suggestion-message">
              Add a <b>{balancingSide}</b> of <b>{fmtUSD(balancingAmount)}</b> to balance this entry.
            </span>
            <select
              value={balanceAccount}
              onChange={(event) => setBalanceAccount(event.target.value)}
              className="ml-auto h-7 min-w-48 rounded-sm border border-border bg-background px-2 text-[11px]"
              data-testid="ledger-balance-suggestion-account"
            >
              <option value="">Choose account…</option>
              {accounts.map((account) => (
                <option key={account.code} value={account.code}>{account.code} · {account.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={addBalancingLine}
              disabled={!balanceAccount}
              className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11px] font-semibold text-primary-foreground disabled:opacity-50"
              data-testid="ledger-balance-suggestion-apply"
            >
              <PlusCircle className="h-3 w-3" /> Add {balancingSide}
            </button>
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button" onClick={addLine}
            className="inline-flex h-7 items-center gap-1 rounded-sm border border-border bg-background px-2 text-[11.5px] shadow-btn hover:shadow-btn-hover"
            data-testid="ledger-entry-add-line"
          >
            <PlusCircle className="h-3 w-3" /> Add line
          </button>
          <span className="ml-auto font-mono-stat text-[11.5px]">
            Debits: <span className="text-primary">{fmtUSD(debitTotal)}</span>
            {'  '}Credits: <span className="text-primary">{fmtUSD(creditTotal)}</span>
          </span>
          <span className={`rounded-sm px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${
            Math.abs(debitTotal - creditTotal) < 0.005 && debitTotal > 0
              ? 'bg-success/20 text-success'
              : 'bg-muted text-muted-foreground'
          }`}>
            {debitTotal === 0 ? 'Empty' : Math.abs(debitTotal - creditTotal) < 0.005 ? 'Balanced' : `Δ ${fmtUSD(debitTotal - creditTotal)}`}
          </span>
          <span
            id="ledger-entry-validation-message"
            className={`basis-full text-right text-[10.5px] ${canSubmit ? 'text-success' : 'text-muted-foreground'}`}
            data-testid="ledger-entry-validation"
            role="status"
          >
            {entryValidation.message}
          </span>
          <button
            type="button" onClick={submit} disabled={saving}
            aria-describedby="ledger-entry-validation-message"
            title={canSubmit ? 'Post this journal entry' : entryValidation.message}
            className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded-sm px-3 text-[12px] font-semibold shadow-btn transition-[background-color,color,opacity] hover:shadow-btn-hover disabled:opacity-60 ${
              canSubmit ? 'bg-primary text-primary-foreground' : 'border border-border bg-secondary text-foreground'
            }`}
            data-testid="ledger-entry-submit"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookText className="h-3 w-3" />}
            Post entry
          </button>
        </div>
      </div>

      {/* Recent Entries */}
      <div className="rounded-md border border-border bg-card p-3" data-testid="ledger-recent-entries">
        <div className="mb-2 flex items-center gap-2">
          <Filter className="h-4 w-4 text-primary" />
          <div className="text-[12.5px] font-semibold">Recent Entries</div>
          {filterAcct && (
            <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 font-mono-stat text-[10.5px] text-primary" data-testid="ledger-filter-pill">
              filtered · {filterAcct}
              <button onClick={() => setFilterAcct('')} className="ml-1 hover:text-destructive" data-testid="ledger-filter-clear">×</button>
            </span>
          )}
          <span className="ml-auto text-[10.5px] text-muted-foreground">{entries.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead className="bg-panel-header/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left">Date</th>
                <th className="px-2 py-1 text-left">Memo</th>
                <th className="px-2 py-1 text-left">Reference</th>
                <th className="px-2 py-1 text-left">Lines</th>
                <th className="px-2 py-1 text-right">Amount</th>
                <th className="px-2 py-1 text-left">Status</th>
                <th className="px-2 py-1 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border/60 align-top" data-testid={`ledger-entry-row-${e.id}`}>
                  <td className="whitespace-nowrap px-2 py-1 font-mono-stat text-[10.5px] text-muted-foreground">
                    {new Date(e.at).toLocaleString()}
                  </td>
                  <td className="px-2 py-1">{e.memo}</td>
                  <td className="px-2 py-1 text-muted-foreground">{e.reference || '—'}</td>
                  <td className="px-2 py-1">
                    <div className="space-y-0.5">
                      {(e.lines || []).map((l, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10.5px]">
                          <span className="font-mono-stat text-muted-foreground">{l.account_code}</span>
                          {(l.debit_usd  > 0) && <span className="text-primary">Dr {fmtUSD(l.debit_usd)}</span>}
                          {(l.credit_usd > 0) && <span className="text-success">Cr {fmtUSD(l.credit_usd)}</span>}
                          {l.memo && <span className="text-muted-foreground">· {l.memo}</span>}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right font-mono-stat">{fmtUSD(e.total_usd)}</td>
                  <td className="px-2 py-1 text-[10.5px]">
                    {e.reversal_id ? `Reversed · ${e.reversal_id}` : e.reversal_of ? `Reversal · ${e.reversal_of}` : e.status || 'posted'}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {!e.reversal_id && !e.reversal_of && (e.status === 'posted' || !e.status) && (
                      <button
                        type="button"
                        onClick={() => correctEntry(e)}
                        disabled={correcting === e.id}
                        className="inline-flex h-7 items-center gap-1 rounded-sm border border-border px-2 text-[10.5px] hover:border-primary disabled:opacity-60"
                        data-testid={`ledger-correct-${e.id}`}
                      >
                        {correcting === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                        Correct
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-4 text-center text-[11.5px] text-muted-foreground">No entries yet — post your first journal above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
    </ResizableLedgerWorkspace>
  );
}
